/**
 * Level 2: 姿态 + BCS (spec 2.3, plan 3.2–3.4)
 * MediaPipe Pose Landmarker → 肩 [11,12] 髋 [23,24] → Hip/Shoulder 比 → BCS 描述
 * 注：为人姿态模型，用于狗为近似；关键点不足时返回「无法评估」
 */

import { PoseLandmarker, FilesetResolver } from '@mediapipe/tasks-vision';

const WASM =
  'https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision@latest/wasm';
// 与 Object Detector 同源；若加载失败 BCS 会回退为「体型评估暂不可用」
const MODEL =
  'https://storage.googleapis.com/mediapipe-tasks/pose_landmarker/pose_landmarker_lite.task';

let poseLandmarker: PoseLandmarker | null = null;

const LANDMARK = {
  LEFT_SHOULDER: 11,
  RIGHT_SHOULDER: 12,
  LEFT_HIP: 23,
  RIGHT_HIP: 24,
} as const;

function dist(
  a: { x: number; y: number },
  b: { x: number; y: number }
): number {
  return Math.hypot(a.x - b.x, a.y - b.y);
}

export interface BCSResult {
  health: string;
  healthMeta: string;
}

export async function initPoseLandmarker(): Promise<void> {
  if (poseLandmarker) return;
  try {
    const vision = await FilesetResolver.forVisionTasks(WASM);
    poseLandmarker = await PoseLandmarker.createFromOptions(vision, {
      baseOptions: { modelAssetPath: MODEL },
      runningMode: 'IMAGE',
      numPoses: 1,
    });
  } catch (e) {
    console.warn('Pose model load failed, BCS will fallback:', e);
  }
}

export function computeBCS(
  imageSource: HTMLVideoElement | HTMLCanvasElement
): BCSResult {
  if (!poseLandmarker) {
    return { health: '体型评估暂不可用', healthMeta: '请稍后重试' };
  }
  try {
    const result = poseLandmarker.detect(imageSource);
    const poses = result.landmarks ?? [];
    if (poses.length === 0) {
      return { health: '无法评估', healthMeta: '请将宠物侧面对准镜头' };
    }
    const lm = poses[0];
    const get = (i: number) => ({ x: lm[i]?.x ?? 0, y: lm[i]?.y ?? 0 });
    const ls = get(LANDMARK.LEFT_SHOULDER);
    const rs = get(LANDMARK.RIGHT_SHOULDER);
    const lh = get(LANDMARK.LEFT_HIP);
    const rh = get(LANDMARK.RIGHT_HIP);
    const shoulderW = dist(ls, rs);
    const hipW = dist(lh, rh);
    if (shoulderW < 1e-5) {
      return { health: '无法评估', healthMeta: '关键点不足' };
    }
    const ratio = hipW / shoulderW;
    // 经验映射：比值偏大偏胖，偏小偏瘦（参考 VCA BCS 思路，此处为简化）
    if (ratio < 0.85) {
      return { health: '偏瘦', healthMeta: '可以适当增加营养与运动' };
    }
    if (ratio > 1.15) {
      return { health: '偏胖', healthMeta: '建议控制饮食、增加活动量' };
    }
    return { health: '体型刚好', healthMeta: '保持现在的运动量就很好' };
  } catch {
    return { health: '无法评估', healthMeta: '姿态识别失败' };
  }
}

/** 返回单帧姿态关键点，供多帧情绪计算（尾部近似用髋部） */
export interface PoseLandmarksFrame {
  timestamp: number;
  landmarks: Array<{ x: number; y: number; z?: number }>;
}

export function getPoseLandmarks(
  imageSource: HTMLVideoElement | HTMLCanvasElement
): PoseLandmarksFrame | null {
  if (!poseLandmarker) return null;
  try {
    const result = poseLandmarker.detect(imageSource);
    const poses = result.landmarks ?? [];
    if (poses.length === 0) return null;
    const lm = poses[0].map((p) => ({ x: p.x, y: p.y, z: p.z }));
    return { timestamp: Date.now(), landmarks: lm };
  } catch {
    return null;
  }
}
